import type { PrintResult, SaleVerdict, ShiftTotals } from '@krp/client-runtime'
import type { OwnCommandResult, OwnTotalsView } from '../ui/OwnActionsDialog'
import { formatKopecks } from '@krp/sale-ui'

import type { AgentSessionModel } from '../../agent-session/model/agentSessionController'

/**
 * Что диалог «Действия» источника «Собственная» показывает кассиру: слова смены, итоги смены и итог команды. Чистые функции от ответов
 * агента; тексты агента (`error.text`, `error.action`, проблемы кассы) — дословно (ADR-8), свои слова только там, где агент молчит.
 */

/** «Смена открыта, №3» / «Смена закрыта» / «Смена истекла: прошло больше 24 часов» / «Состояние смены не подтверждено». */
export function shiftText(session: Pick<AgentSessionModel, 'device' | 'deviceUnreachable'>): string {
  if (session.deviceUnreachable || session.device === null) return 'Состояние смены не подтверждено'
  const shift = session.device.shift
  switch (shift?.state) {
    case 'opened':
      return shift.number === null ? 'Смена открыта' : `Смена открыта, №${shift.number}`
    case 'closed':
      return 'Смена закрыта'
    case 'expired':
      return 'Смена истекла: прошло больше 24 часов, закройте её и откройте новую'
    default:
      return 'Состояние смены не подтверждено'
  }
}

/** Замечания кассы про смену словами агента (коды `shift-…`): закрыта, истекла, скоро истечёт. */
export function shiftProblems(session: Pick<AgentSessionModel, 'device'>): readonly string[] {
  return session.device?.problems.filter((problem) => problem.code.startsWith('shift-')).map((problem) => problem.text) ?? []
}

const rubles = (value: number | null): string | null => (value === null ? null : formatKopecks(Math.round(value * 100)))

/** Итоги смены для панели «Смена»: только то, что касса дала; чего нет — строки нет, а не «0». */
export function totalsView(totals: ShiftTotals | null, loading: boolean): OwnTotalsView {
  if (loading) return { status: 'loading', facts: [], error: null }
  if (totals === null) return { status: 'idle', facts: [], error: null }
  const facts: { label: string; value: string }[] = []
  const add = (label: string, value: string | null): void => {
    if (value !== null) facts.push({ label, value })
  }
  const receipts = (label: string, part: ShiftTotals['sales']): void => {
    if (part === null) return
    const sum = rubles(part.sum)
    add(label, part.count === null && sum === null ? null : [part.count === null ? null : `${part.count} шт`, sum].filter((item) => item !== null).join(' на '))
    for (const [kind, amount] of Object.entries(part.payments)) {
      const text = rubles(amount)
      if (text !== null && amount > 0) add(`  · ${PAYMENT_LABELS[kind] ?? kind}`, text)
    }
  }
  if (totals.shiftNumber !== null) add('Смена', `№${totals.shiftNumber}`)
  receipts('Продажи', totals.sales)
  receipts('Возвраты', totals.returns)
  if (totals.cashIn !== null && totals.cashIn.count !== null && totals.cashIn.count > 0) add('Внесено', rubles(totals.cashIn.sum))
  if (totals.cashOut !== null && totals.cashOut.count !== null && totals.cashOut.count > 0) add('Выплачено', rubles(totals.cashOut.sum))
  add('Наличные в ящике', rubles(totals.cashInDrawer))
  return { status: 'ready', facts, error: null }
}

const PAYMENT_LABELS: Readonly<Record<string, string>> = { cash: 'наличными', electronically: 'безналичными', prepaid: 'предоплатой', credit: 'в кредит', other: 'иначе' }

/** Итог чтения итогов, не вышедшего: слова агента из уведомления контроллера. */
export function totalsFailure(session: Pick<AgentSessionModel, 'notice'>): OwnTotalsView {
  return { status: 'failed', facts: [], error: session.notice?.text ?? 'Итоги смены не прочитаны.' }
}

/**
 * Итог команды кассе для диалога. `null` — команду не отправили (касса занята или закрыта порогом): причина — в уведомлении
 * контроллера. «Неизвестно» остаётся предупреждением, а не успехом и не отказом.
 */
export function commandResult(verdict: SaleVerdict | null, doneText: string, session: Pick<AgentSessionModel, 'notice'>): OwnCommandResult {
  if (verdict === null) {
    return { tone: 'warning', text: session.notice?.text ?? 'Сейчас эту операцию выполнить нельзя.', advice: null }
  }
  switch (verdict.kind) {
    case 'sold': {
      const drawer = verdict.cashSum === null ? '' : ` Наличных в кассе: ${rubles(verdict.cashSum) ?? ''}.`
      return { tone: 'success', text: doneText + drawer, advice: null }
    }
    case 'not-sold':
      return { tone: 'danger', text: verdict.error?.text ?? 'Операция не выполнена.', advice: verdict.error?.action ?? null }
    case 'unknown':
      return {
        tone: 'warning',
        text: verdict.error?.text ?? 'Исход операции неизвестен.',
        advice: 'Не повторяйте операцию: проверьте кассу, окно выяснит исход само.',
      }
  }
}

/** Итог печати (X-отчёт, копия чека). `null` — не отправили. */
export function printResult(result: PrintResult | null, doneText: string, session: Pick<AgentSessionModel, 'notice'>): OwnCommandResult {
  if (result === null) return { tone: 'warning', text: session.notice?.text ?? 'Сейчас печатать нельзя.', advice: null }
  if (result.status === 'done') return { tone: 'success', text: doneText, advice: null }
  return { tone: 'danger', text: result.error?.text ?? 'Печать не удалась.', advice: result.error?.action ?? null }
}
